// Kształt odpowiedzi `GET /api/v1/admin/scraper/field-quality`.
//
// Sekcja odpowiada na pytanie, na które reszta diagnostyki jest ŚLEPA: liczniki
// (`created`/`revised`/`failed`) pokażą „zdrowo" także wtedy, gdy źródło zapisze komplet
// rekordów z PUSTYM polem — bo to nadal `created`. Realny bug tej klasy: zmiana layoutu
// portalu → przestał się parsować `organizer` („Wystawiający").
//
// Liczby biorą się wprost z kolekcji `auctions`, NIE z telemetrii — scraper może kłamać
// (bug w mapperze), zawartość dokumentu nie.

/**
 * Ocena komórki (źródło × pole).
 * - `debt` — puste OD ZAWSZE (np. Platforma Zakupowa nie zapisuje miasta). To znany dług
 *   scrapera, nie regresja: alarmowanie go świeciłoby czerwono na zawsze i nauczyło
 *   operatora ignorować panel. Renderujemy szaro, osobno od alertów. Gdy dług zostanie
 *   spłacony, komórka sama wychodzi do `ok` — poprawa nigdy nie jest alarmem.
 * - `insufficient` — za mała próbka ALBO pomiar obciążony dojrzałością kohorty
 *   (`maturity_sensitive`). Zawsze „—", nigdy zero.
 */
export type FieldStatus = "ok" | "warn" | "crit" | "debt" | "insufficient";

export const FIELD_STATUS_LABELS: Record<FieldStatus, string> = {
  ok: "OK",
  warn: "Spadek",
  crit: "Regresja",
  debt: "Dług",
  insufficient: "Brak sygnału",
};

export interface FieldQualityDaily {
  /** `YYYY-MM-DD`. */
  day: string;
  /** `null` = w tym dniu nic nie wpadło — to NIE jest 0% wypełnienia. */
  pct: number | null;
  total: number;
}

export interface FieldQualityCell {
  field: string;
  label: string;
  status: FieldStatus;
  /** Powód oceny po polsku, gotowy do wyświetlenia (z liczbami, żeby dało się sprawdzić). */
  reason: string;
  /** `null` = brak próbki. Front MUSI pokazać „—", nie „0%". */
  current_pct: number | null;
  current_filled: number;
  current_total: number;
  /** Mediana z dni baseline'u. `null` = baseline nie dał się policzyć. */
  baseline_pct: number | null;
  /** Rozrzut baseline'u (MAD) — o tyle pole waha się NORMALNIE. */
  baseline_mad: number | null;
  baseline_days: number;
  drop_pp: number | null;
  /** Próg, powyżej którego spadek jest alarmem: `max(10 pp, 3 × MAD)`. */
  threshold_pp: number | null;
  daily: FieldQualityDaily[];
}

/** Komórka wyniesiona na listę regresji — jak `FieldQualityCell`, ale wie, z jakiego źródła. */
export interface FieldQualityRegression extends FieldQualityCell {
  source_name: string;
}

export interface FieldQualitySource {
  /** Kanoniczna nazwa (`auctions.source_name`), np. „Platforma Zakupowa”. */
  source_name: string;
  records: number;
  cells: FieldQualityCell[];
}

export interface FieldQualityField {
  key: string;
  label: string;
  /**
   * Pole dopełnia się asynchronicznie już po utworzeniu ogłoszenia (załączniki dociąga
   * `attachment_service`). Świeża kohorta ma go z natury mniej niż dojrzały baseline, więc
   * panel je MIERZY i pokazuje, ale nie oskarża — nie orzekamy na pomiarze, o którym wiemy,
   * że jest obciążony.
   */
  maturity_sensitive: boolean;
}

export interface FieldQualityResponse {
  generated_at: string;
  window: { since: string; until: string; minutes: number };
  /** Historia, z której liczona jest mediana. Kończy się tam, gdzie zaczyna się okno. */
  baseline: { days: number; since: string; until: string };
  /** `false` = agregacja padła → „brak danych”, NIGDY zera na heatmapie. */
  available: boolean;
  fields: FieldQualityField[];
  summary: { ok: number; warn: number; crit: number; debt: number; insufficient: number };
  regressions: FieldQualityRegression[];
  sources: FieldQualitySource[];
  thresholds: {
    min_current_sample: number;
    min_daily_sample: number;
    min_baseline_days: number;
    debt_max_pct: number;
    min_drop_pp: number;
    mad_k: number;
    min_rel_drop: number;
    min_absolute_miss: number;
    vanish_ratio: number;
    crit_drop_pp: number;
  };
}
